import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["active", "showActiveCls", "suffixIcon", "format", "validateFormat", "onChange", "onInput", "helped", "onHelp", "onSubmit", "onKeyDown", "onKeyUp", "preserveInvalidOnBlur", "invalid", "clearIcon"];
import classNames from 'classnames';
import { useEvent } from 'dw-neit-rc-util';
import useLayoutEffect from "dw-neit-rc-util/es/hooks/useLayoutEffect";
import raf from "dw-neit-rc-util/es/raf";
import * as React from 'react';
import { leftPad } from "../../utils/miscUtil";
import PickerContext from "../context";
import useLockEffect from "../hooks/useLockEffect";
import Icon from "./Icon";
import MaskFormat from "./MaskFormat";
import { getMaskRange } from "./util";

// Format logic
//
// First time on focus:
//  1. check if the text is valid, if not fill with format
//  2. set highlight cell to the first cell
// Cells
//  1. Selection the index cell, set inner `cacheValue` to ''
//  2. Key input filter non-number char, patch after the `cacheValue`
//    1. Replace the `cacheValue` with input align the cell length
//    2. Re-selection the mask cell
//  3. If `cacheValue` match the limit length or cell format (like 1 ~ 12 month), go to next cell

var Input = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var active = props.active,
    _props$showActiveCls = props.showActiveCls,
    showActiveCls = _props$showActiveCls === void 0 ? true : _props$showActiveCls,
    suffixIcon = props.suffixIcon,
    format = props.format,
    validateFormat = props.validateFormat,
    onChange = props.onChange,
    onInput = props.onInput,
    helped = props.helped,
    onHelp = props.onHelp,
    onSubmit = props.onSubmit,
    onKeyDown = props.onKeyDown,
    onKeyUp = props.onKeyUp,
    _props$preserveInvali = props.preserveInvalidOnBlur,
    preserveInvalidOnBlur = _props$preserveInvali === void 0 ? false : _props$preserveInvali,
    invalid = props.invalid,
    clearIcon = props.clearIcon,
    restProps = _objectWithoutProperties(props, _excluded);
  var value = props.value,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    onMouseUp = props.onMouseUp;
  var _React$useContext = React.useContext(PickerContext),
    prefixCls = _React$useContext.prefixCls,
    _React$useContext$inp = _React$useContext.input,
    Component = _React$useContext$inp === void 0 ? 'input' : _React$useContext$inp;
  var inputPrefixCls = "".concat(prefixCls, "-input");

  // ======================== Value =========================
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    focused = _React$useState2[0],
    setFocused = _React$useState2[1];
  var _React$useState3 = React.useState(value),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    internalInputValue = _React$useState4[0],
    setInputValue = _React$useState4[1];
  //----dareway----专门为了增加的输入数字键时不补0增加的，自动补零会导致在年的时候只输入2就触发onchange
  var _React$useState5 = React.useState(value),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    inputDisPlayValue = _React$useState6[0],
    setInputDisPlayValue = _React$useState6[1];
  var _React$useState7 = React.useState(''),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    focusCellText = _React$useState8[0],
    setFocusCellText = _React$useState8[1];
  var _React$useState9 = React.useState(null),
    _React$useState10 = _slicedToArray(_React$useState9, 2),
    focusCellIndex = _React$useState10[0],
    setFocusCellIndex = _React$useState10[1]; // ----dareway----新增了all值，专门用来标记全选状态，antdesign原本不可能让input中的值全选
  var _React$useState11 = React.useState(null),
    _React$useState12 = _slicedToArray(_React$useState11, 2),
    forceSelectionSyncMark = _React$useState12[0],
    forceSelectionSync = _React$useState12[1];
  var inputValue = internalInputValue || '';

  // Sync value if needed
  React.useEffect(function () {
    setInputValue(value);
    setInputDisPlayValue(value);
  }, [value]);

  // ========================= Refs =========================
  var holderRef = React.useRef();
  var inputRef = React.useRef();
  var clickTimeoutRef = React.useRef(null); // ----dareway----使用 useRef 来存储计时器 ID

  React.useImperativeHandle(ref, function () {
    return {
      nativeElement: holderRef.current,
      inputElement: inputRef.current,
      focus: function focus(options) {
        inputRef.current.focus(options);
      },
      blur: function blur() {
        inputRef.current.blur();
      }
    };
  });

  // ======================== Format ========================
  var maskFormat = React.useMemo(function () {
    return new MaskFormat(format || '');
  }, [format]);
  var _React$useMemo = React.useMemo(function () {
      if (helped) {
        return [0, 0];
      }

      //----dareway----当发现focusCellIndex为all时，说明Input值全选了，直接返回[0, format.length]即可
      if (focusCellIndex == 'all') {
        return [0, format.length];
      }
      return maskFormat.getSelection(focusCellIndex);
    }, [maskFormat, focusCellIndex, helped]),
    _React$useMemo2 = _slicedToArray(_React$useMemo, 2),
    selectionStart = _React$useMemo2[0],
    selectionEnd = _React$useMemo2[1];

  //----dareway----专门为了增加的输入数字键时不补0增加的
  var _React$useMemo3 = React.useMemo(function () {
      if (helped) {
        return [0, 0];
      }
      if (focusCellIndex == 'all') {
        return [0, format.length];
      }
      if (inputDisPlayValue != inputValue) {
        var maskFormatSelection = maskFormat.getSelection(focusCellIndex);
        var result = inputDisPlayValue.split(/[- :]/).map(String);
        return [maskFormatSelection[0], maskFormatSelection[0] + result[focusCellIndex].length];
      } else {
        return maskFormat.getSelection(focusCellIndex);
      }
    }, [maskFormat, focusCellIndex, helped, inputDisPlayValue]),
    _React$useMemo4 = _slicedToArray(_React$useMemo3, 2),
    disPlayInputSelectionStart = _React$useMemo4[0],
    disPlayInputSelectionEnd = _React$useMemo4[1];

  // ======================== Modify ========================
  // When input modify content, trigger `onHelp` if is not the format
  var onModify = function onModify(text) {
    if (text && text !== format && text !== value) {
      onHelp();
    }
  };

  // ======================== Change ========================
  /**
   * Triggered by paste, keyDown and focus to show format
   */
  var triggerInputChange = useEvent(function (text) {
    if (validateFormat(text)) {
      onChange(text);
    }
    setInputValue(text);
    setInputDisPlayValue(text);
    onModify(text);
  });

  //----dareway----专门为了增加的输入数字键时不补0增加的
  var triggerDisPlayInputChange = useEvent(function (text, nextFocusValue) {
    if (validateFormat(text)) {
      onChange(text);
    }
    setInputValue(nextFocusValue);
    setInputDisPlayValue(text);
    onModify(nextFocusValue);
  });

  // Directly trigger `onChange` if `format` is empty
  var onInternalChange = function onInternalChange(event) {
    // Hack `onChange` with format to do nothing
    if (!format) {
      var text = event.target.value;
      onModify(text);
      setInputValue(text);
      setInputDisPlayValue(text);
      onChange(text);
    }
  };
  var onFormatPaste = function onFormatPaste(event) {
    // Get paste text
    var pasteText = event.clipboardData.getData('text');
    if (validateFormat(pasteText)) {
      triggerInputChange(pasteText);
    }
  };

  // ======================== Mouse =========================
  // When `mouseDown` get focus, it's better to not to change the selection
  // Since the up position maybe not is the first cell
  var mouseDownRef = React.useRef(false);
  var onFormatMouseDown = function onFormatMouseDown() {
    mouseDownRef.current = true;
  };
  var onFormatMouseUp = function onFormatMouseUp(event) {
    var selectedText = window.getSelection().toString();
    var _ref = event.target,
      start = _ref.selectionStart;

    //----dareway----因为是在MouseUp中设置focusCellIndex去决定input中谁被选中，那么想要实现双击全选中input，只能在MouseUp中模拟双击
    if (clickTimeoutRef.current) {
      //----dareway----当判定为双击时，清理定时器，重置计时器 ID，并设置为全选
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
      setFocusCellIndex("all");
    } else {
      // 设置一个计时器，假设双击的时间间隔为 300ms
      clickTimeoutRef.current = setTimeout(function () {
        clickTimeoutRef.current = null; // 重置计时器 ID
      }, 300);
      //----dareway----当判定为单击时，如果之前不是全选状态，通过此次单击置为全选状态时，设置为全选
      if (selectedText.length == format.length && (selectionStart != 0 || selectionEnd != format.length)) {
        setFocusCellIndex("all");
      } else {
        //----dareway----当判定为单击时，且此次单击未造成全选状态，只选中点击处部分
        var closeMaskIndex = maskFormat.getMaskCellIndex(start);
        setFocusCellIndex(closeMaskIndex);
      }
    }

    // Force update the selection
    forceSelectionSync({});
    onMouseUp === null || onMouseUp === void 0 || onMouseUp(event);
    mouseDownRef.current = false;
  };

  // ====================== Focus Blur ======================
  var onFormatFocus = function onFormatFocus(event) {
    setFocused(true);
    setFocusCellIndex(0);
    setFocusCellText('');
    onFocus(event);
  };
  var onSharedBlur = function onSharedBlur(event) {
    onBlur(event);
  };
  var onFormatBlur = function onFormatBlur(event) {
    setFocused(false);
    onSharedBlur(event);
  };

  // ======================== Active ========================
  // Check if blur need reset input value
  useLockEffect(active, function () {
    if (!active && !preserveInvalidOnBlur) {
      /*---dareway修改----
      修改原因：当blur时，input中的值是YYYY-MM-DD时，说明已经将日期值清空了，应该把Input的值置空,但是不需要blur时触发onchange事件，onchange在按backspace后就触发
      */
      if (inputValue != format) {
        setInputValue(value);
        setInputDisPlayValue(value);
      } else {
        setInputValue("");
        setInputDisPlayValue("");
      }
    }
  });

  // ======================= Keyboard =======================
  var onSharedKeyDown = function onSharedKeyDown(event) {
    if (event.key === 'Enter' && validateFormat(inputValue)) {
      onSubmit();
    }
    onKeyDown === null || onKeyDown === void 0 || onKeyDown(event);
  };
  var handlekeyUp = function handlekeyUp(event) {
    onKeyUp === null || onKeyUp === void 0 || onKeyUp(event);
  };
  var onFormatKeyDown = function onFormatKeyDown(event) {
    //----dareway----当触发onkeydown的时候，默认已经mouseUp了,将mouseDownRef设置为false;
    //为什么这样做？  当input  mouseDown后选中input中的一些内容然后离开input进行mouseUp，此时没办法触发input内的mouseUp去修改FocusCellIndex。会导致一系列问题，比如触发左键没办法选中第一个format等等。在此处修改时最简便的方法
    mouseDownRef.current = false;
    onSharedKeyDown(event);
    var key = event.key;

    // Save the cache with cell text
    var nextCellText = null;

    // Fill in the input
    var nextFillText = null;
    var maskCellLen = selectionEnd - selectionStart;
    var cellFormat = format.slice(selectionStart, selectionEnd);

    // Cell Index
    var offsetCellIndex = function offsetCellIndex(offset) {
      setFocusCellIndex(function (idx) {
        if (typeof idx == 'number') {
          var nextIndex = idx + offset;
          nextIndex = Math.max(nextIndex, 0);
          nextIndex = Math.min(nextIndex, maskFormat.size() - 1);
          return nextIndex;
        }
      });
    };

    // Range
    var offsetCellValue = function offsetCellValue(offset) {
      var _getMaskRange = getMaskRange(cellFormat),
        _getMaskRange2 = _slicedToArray(_getMaskRange, 3),
        rangeStart = _getMaskRange2[0],
        rangeEnd = _getMaskRange2[1],
        rangeDefault = _getMaskRange2[2];
      var currentText = inputValue.slice(selectionStart, selectionEnd);
      var currentTextNum = Number(currentText);
      if (isNaN(currentTextNum)) {
        return String(rangeDefault ? rangeDefault : offset > 0 ? rangeStart : rangeEnd);
      }
      var num = currentTextNum + offset;
      var range = rangeEnd - rangeStart + 1;
      return String(rangeStart + (range + num - rangeStart) % range);
    };
    switch (key) {
      // =============== Remove ===============
      case 'Backspace':
      case 'Delete':
        nextCellText = '';
        nextFillText = cellFormat;
        //----dareway----当全选中的时候触发删除键，input中会展示format，并选中format的第一段
        if (window.getSelection().toString().length == format.length) {
          nextFillText = format;
          //为什么要选中format的第一段？？因为在此模式中，必须有一段是被选中的，不然没办法继续修改Input值了
          setFocusCellIndex(0);
        }
        break;

      // =============== Arrows ===============
      // Left key
      case 'ArrowLeft':
        nextCellText = '';
        //----dareway----当全选中的时候触发左键，选中format的第一段
        if (window.getSelection().toString().length == format.length) {
          setFocusCellIndex(0);
        } else {
          offsetCellIndex(-1);
        }
        break;

      // Right key
      case 'ArrowRight':
        nextCellText = '';
        //----dareway----当全选中的时候触发右键，选中format的最后一段
        if (window.getSelection().toString().length == format.length) {
          var closeMaskIndex = maskFormat.getMaskCellIndex(format.length);
          setFocusCellIndex(closeMaskIndex);
        } else {
          offsetCellIndex(1);
        }
        break;

      // Up key
      case 'ArrowUp':
        //----dareway----当全选中的时候触发上键，选中format的第一段
        // 此处注意，要排除format只有一段的情况，否则触发上键永远只能全选中，没办法修改值了
        if (window.getSelection().toString().length == format.length && maskFormat.getSelection(0)[1] != format.length) {
          setFocusCellIndex(0);
        } else {
          nextCellText = '';
          nextFillText = offsetCellValue(1);
        }
        break;

      // Down key
      case 'ArrowDown':
        //----dareway----当全选中的时候触发下键，选中format的第一段
        // 此处注意，要排除format只有一段的情况，否则触发下键永远只能全选中，没办法修改值了
        if (window.getSelection().toString().length == format.length && maskFormat.getSelection(0)[1] != format.length) {
          setFocusCellIndex(0);
        } else {
          nextCellText = '';
          nextFillText = offsetCellValue(-1);
        }
        break;

      // =============== Number ===============
      default:
        if (!isNaN(Number(key))) {
          //----dareway----当全选中的时候触发数字键（排除了format只有一段的情况），选中format的第一段，且可以进行输入
          // 此处注意，为了避免其他可能出现的bug，排除了format只有一段的情况，不排除也可以的
          if (window.getSelection().toString().length == format.length && maskFormat.getSelection(0)[1] != format.length) {
            //选中format的第一段
            setFocusCellIndex(0);
            //设置第一段的值
            setFocusCellText(focusCellText + key);
            //设置补零后的值
            var nextFocusValue = leftPad(focusCellText + key, maskFormat.getSelection(0)[1]) + format.slice(maskFormat.getSelection(0)[1]);

            //设置不补零后的值
            var nextFocusDisPlayValue = focusCellText + key + format.slice(maskFormat.getSelection(0)[1]);
            triggerDisPlayInputChange(nextFocusDisPlayValue.slice(0, format.length), nextFocusValue.slice(0, format.length));
            break;
          } else if (maskFormat.getSelection(0)[1] == format.length) {
            //----dareway----当format只有一段的情况全选中时候触发数字键，要设置选中format的第一段，否则当双击全选中后直接输入完数据，值却没有全选中
            setFocusCellIndex(0);
          }
          nextCellText = focusCellText + key;
          nextFillText = nextCellText;
        }
        break;
    }

    // Update cell text
    if (nextCellText !== null) {
      setFocusCellText(nextCellText);
      if (nextCellText.length >= maskCellLen) {
        // Go to next cell
        offsetCellIndex(1);
        setFocusCellText('');
      }
    }

    // Update the input text
    if (nextFillText !== null) {
      // Replace selection range with `nextCellText`
      var _nextFocusValue =
      // before
      inputValue.slice(0, selectionStart) +
      // replace
      leftPad(nextFillText, maskCellLen) +
      // after
      inputValue.slice(selectionEnd);

      //----dareway----当触发方向键时nextCellText == ""，不涉及输入数字键的事，故而不需要触发triggerDisPlayInputChange，触发数字键时，触发triggerDisPlayInputChange事件
      if (nextCellText == "") {
        triggerInputChange(_nextFocusValue.slice(0, format.length));
      } else {
        var _nextFocusDisPlayValue =
        // before
        inputDisPlayValue.slice(0, disPlayInputSelectionStart) +
        // replace
        nextFillText +
        // after
        inputDisPlayValue.slice(disPlayInputSelectionEnd);
        triggerDisPlayInputChange(_nextFocusDisPlayValue.slice(0, format.length), _nextFocusValue.slice(0, format.length));
      }
      //----dareway----当触发删除键后，当前值和format一致且原本inputValue值不为format，那么触发onChange事件
      if (_nextFocusValue.slice(0, format.length) == format && inputValue != format) {
        onChange("");
      }
    }

    // Always trigger selection sync after key down
    forceSelectionSync({});
  };

  // ======================== Format ========================
  var rafRef = React.useRef();
  useLayoutEffect(function () {
    if (!focused || !format || mouseDownRef.current) {
      return;
    }

    // Reset with format if not match
    if (!maskFormat.match(inputValue)) {
      triggerInputChange(format);
      return;
    }

    // Match the selection range
    if (inputDisPlayValue == inputValue) {
      inputRef.current.setSelectionRange(selectionStart, selectionEnd);

      // Chrome has the bug anchor position looks not correct but actually correct
      rafRef.current = raf(function () {
        inputRef.current.setSelectionRange(selectionStart, selectionEnd);
      });
    } else {
      //----dareway----当触发数字键后，input中不会自动补0，所以选中的长度就是目前已经输入的长度
      inputRef.current.setSelectionRange(disPlayInputSelectionStart, disPlayInputSelectionEnd);

      // Chrome has the bug anchor position looks not correct but actually correct
      rafRef.current = raf(function () {
        inputRef.current.setSelectionRange(disPlayInputSelectionStart, disPlayInputSelectionEnd);
      });
    }
    return function () {
      raf.cancel(rafRef.current);
    };
  }, [maskFormat, format, focused, inputValue, inputDisPlayValue, focusCellIndex, selectionStart, selectionEnd, disPlayInputSelectionStart, disPlayInputSelectionEnd, forceSelectionSyncMark, triggerInputChange]);

  // ======================== Render ========================
  // Input props for format
  var inputProps = format ? {
    onFocus: onFormatFocus,
    onBlur: onFormatBlur,
    onKeyDown: onFormatKeyDown,
    onMouseDown: onFormatMouseDown,
    onMouseUp: onFormatMouseUp,
    onPaste: onFormatPaste
  } : {};
  return /*#__PURE__*/React.createElement("div", {
    ref: holderRef,
    className: classNames(inputPrefixCls, _defineProperty(_defineProperty({}, "".concat(inputPrefixCls, "-active"), active && showActiveCls), "".concat(inputPrefixCls, "-placeholder"), helped))
  }, /*#__PURE__*/React.createElement(Component, _extends({
    ref: inputRef,
    "aria-invalid": invalid,
    autoComplete: "off"
  }, restProps, {
    onKeyDown: onSharedKeyDown,
    onKeyUp: handlekeyUp,
    onBlur: onSharedBlur
    // Replace with format
  }, inputProps, {
    // Value
    value: inputDisPlayValue,
    onChange: onInternalChange
  })), /*#__PURE__*/React.createElement(Icon, {
    type: "suffix",
    icon: suffixIcon
  }), clearIcon);
});
if (process.env.NODE_ENV !== 'production') {
  Input.displayName = 'Input';
}
export default Input;